import { cloneDeep } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import type { TextSelectionRange } from '@/utils/text-kit';
import {
    LayerType,
    MaterialIdsType,
    TextWritingType,
    type ILayerProps,
    type ITextLayerProps,
    type ITextMark,
    type Optional,
} from '@/version-types';
import { applyTextRangeAttrs, pushHistory, unifyTextContentsAttrs, updateLayer } from '../utils';

type Options = {
    range?: Optional<TextSelectionRange>;
    writeHistory?: boolean;
};

type Result = { layer: ILayerProps; range: TextSelectionRange | null } | void;

export interface IHighlight {
    setHighlight(layerID: string, value: ITextMark, options?: Pick<Options, 'range'>): Result;
    removeHighlight(layerID: string, options?: Pick<Options, 'range'>): Result;
    updateHighlight(layerID: string, value: ITextMark, options?: Options): Result;
}

const FUNC_ID = trackConfig[LayerType.TEXT_LAYER].mark;

const updateMaterialIDs = (layer: ILayerProps): ILayerProps['materialIds'] => {
    const data = layer.data as ITextLayerProps;
    const ids = [data.textMark, ...(data.textContents || []).map((item) => item.textMark)]
        .filter((item): item is ITextMark => !!item)
        .map((item) => item.materialId)
        .filter((id, index, all) => all.indexOf(id) === index);
    const materialIds = cloneDeep(layer.materialIds || {});

    if (ids.length > 0) materialIds[MaterialIdsType.TEXT_MARK] = ids;
    else delete materialIds[MaterialIdsType.TEXT_MARK];

    return materialIds;
};

const isSupportedLayer = (layer: ILayerProps | null): layer is ILayerProps => {
    if (!layer || layer.layerType !== LayerType.TEXT_LAYER) return false;
    const data = layer.data as ITextLayerProps;
    return (
        !data.shapeExtension &&
        data.writingMode !== TextWritingType.VERTICAL_LR &&
        data.writingMode !== TextWritingType.VERTICAL_RL
    );
};

export class Highlight implements IHighlight {
    private adjustment?: { layerID: string; rangeKey: string; layer: ILayerProps };

    constructor(private framework: IFramework) {}

    private prepare(prevLayer: ILayerProps, value: ITextMark | undefined, range: TextSelectionRange | null) {
        const prevData = prevLayer.data as ITextLayerProps;
        let data: ITextLayerProps = { ...prevData };
        let nextRange: TextSelectionRange | null = null;

        if (value) data.textBackground = [];

        if (range && range.start !== range.end) {
            const result = applyTextRangeAttrs(data, range, { key: 'textMark', value });
            data = { ...data, textMark: null, textContents: result.textContents };
            nextRange = result.range;
        } else {
            data = { ...data, textMark: value };
            if (prevData.textContents?.length) {
                data.textContents = unifyTextContentsAttrs(prevData.textContents, { textMark: value });
            }
        }

        const nextLayer: ILayerProps = { ...prevLayer, data };
        nextLayer.materialIds = updateMaterialIDs(nextLayer);
        return { layer: nextLayer, range: nextRange };
    }

    private set(layerID: string, value: ITextMark | undefined, range: TextSelectionRange | null): Result {
        this.adjustment = undefined;
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!isSupportedLayer(prevLayer)) return;

        const result = this.prepare(prevLayer, value, range);
        if (JSON.stringify(prevLayer) === JSON.stringify(result.layer)) return;

        updateLayer(this.framework, result.layer);
        pushHistory(this.framework, prevLayer, result.layer, FUNC_ID);
        return result;
    }

    setHighlight(layerID: string, value: ITextMark, options: Pick<Options, 'range'> = {}) {
        return this.set(layerID, value, options.range ?? null);
    }

    removeHighlight(layerID: string, options: Pick<Options, 'range'> = {}) {
        return this.set(layerID, undefined, options.range ?? null);
    }

    updateHighlight(layerID: string, value: ITextMark, options: Options = {}): Result {
        const range = options.range ?? null;
        const rangeKey = JSON.stringify(range);
        const current = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!isSupportedLayer(current)) {
            this.adjustment = undefined;
            return;
        }

        if (!this.adjustment || this.adjustment.layerID !== layerID || this.adjustment.rangeKey !== rangeKey) {
            this.adjustment = { layerID, rangeKey, layer: current };
        }

        const result = this.prepare(current, value, range);
        updateLayer(this.framework, result.layer);

        if (options.writeHistory) {
            pushHistory(this.framework, this.adjustment.layer, result.layer, FUNC_ID);
            this.adjustment = undefined;
        }

        return result;
    }
}
